import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { BreadcrumbSetter } from '@/components/Breadcrumbs'
import { DataCard } from '@/components/card'
import TournamentToolbar from '@/components/DataAdmin/Tournaments/TournamentToolbar'
import { PROPERTY_TOURNAMENT_SELECT_TEST_ID } from '@/components/inputs/PropertyTournamentSelect/PropertyTournamentSelect'
import { TableBase } from '@/components/table/base'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM } from '@/router/constants'
import { useTournament } from '@/stores/tournament'
import { GolfTournamentWithFeedCount } from '@/stores/tournaments/tournaments.utils'

import { getTournamentHeaderItems, TOURNAMENT_ENTRY_LIST_TABLE_COLUMNS } from './tournament.table'

const makeTitle = (tournament: GolfTournamentWithFeedCount | null) =>
  `Golf - Tournament ${tournament ? '- ' + tournament?.name : ''}`

export const TournamentView = () => {
  const tournamentId = useParams()[ROUTE_PARAM.TOURNAMENT_ID]

  const { data, tournament, fetchData, isFetching, isLoading, resetState } = useTournament(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching,
      tournament: state.tournament,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    if (tournamentId) fetchData(Number(tournamentId))
    return resetState
  }, [PROPERTY_TOURNAMENT_SELECT_TEST_ID])

  const tableDetails = useMemo(() => {
    return getTournamentHeaderItems(tournament)
  }, [tournament])

  return (
    <>
      <TitleSetter title={makeTitle(tournament)} />
      <BreadcrumbSetter
        param={ROUTE_PARAM.TOURNAMENT_ID}
        label={tournament ? tournament.name : ''}
        isLoading={isLoading}
      />
      <DataCard
        title={`Tournament ${tournament ? '- ' + tournament?.name : ''}`}
        details={tableDetails}
        headerPostfixActions={
          <TournamentToolbar
            tournament={tournament}
            tournamentId={Number(tournamentId)}
            hasWebsockets={
              !!tournament && 'count' in tournament && !!tournament.count.matchByInternalId
            }
            fetchData={() => fetchData(Number(tournamentId))}
          />
        }
      >
        <TableBase
          columns={TOURNAMENT_ENTRY_LIST_TABLE_COLUMNS}
          data={data}
          loading={isLoading}
          fetching={isFetching}
          disableColumnVisibility
          clientSideSortAndPagination
          defaultSort={{
            attribute: 'teamNo',
            order: 'asc'
          }}
        />
      </DataCard>
    </>
  )
}
